/**
 * Grafana Faro Monitoring Initialization
 *
 * 프론트엔드 모니터링을 위한 Grafana Faro SDK 초기화 파일입니다.
 * 모든 페이지에서 가장 먼저 로드되어 에러 및 성능 메트릭을 수집합니다.
 */

import {
  faro,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk';
import {
  TracingInstrumentation,
} from '@grafana/faro-web-tracing';

type FaroConfig<T extends string, N extends string> = {
  url: string;
  app: {
    namespace: T;
    name: N;
    environment: 'production' | 'development';
  };
  isSkip: boolean;
};

const isLocalEnvironment = (() => {
  if (typeof window === 'undefined') return true;
  const { hostname } = window.location;

  return hostname === 'localhost' || hostname === '127.0.0.1';
})();

function createFaroConfig<T extends string, N extends string>(config: {
  url: string;
  app: {
    namespace: T;
    name: N extends `${T}-${string}` ? N : never;
    environment: 'production' | 'development';
  };
  isSkip: boolean;
}): FaroConfig<T, N> {
  return config;
}

const PROJECT_FARO_CONFIG = createFaroConfig({
  url: 'https://frontend-alloy.monitoring.nudge-dev.com/collect',
  app: {
    namespace: 'trost',
    name: 'trost-webapp',
    environment: (() => {
      switch (process.env.NODE_ENV) {
        case 'production':
          return 'production';
        default:
          return 'development';
      }
    })(),
  },
  // 로컬 환경에서는 모니터링 데이터를 수집하지 않도록 해주세요.
  isSkip: isLocalEnvironment,
});

if (!PROJECT_FARO_CONFIG.isSkip && typeof window !== 'undefined' && !faro.api) {
  try {
    initializeFaro({
      url: PROJECT_FARO_CONFIG.url,
      app: PROJECT_FARO_CONFIG.app,
      ignoreUrls: [
        // 무시할 url 패턴 추가

        // 애널리틱스
        /.*analytics\.google\.com.*/,
        /.*google-analytics\.com.*/,
        /.*googletagmanager\.com.*/,

        // 레거시 코드에서 사용되던 외부 sdk 주소
        /.*facebook\.net.*/,
        /.*facebook\.com\/tr.*/,
        /.*kakao\.com.*/,
        /.*channel\.io.*/,
      ],
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
      ],
      sessionTracking: {
        samplingRate: PROJECT_FARO_CONFIG.app.environment === 'production' ? 0.5 : 1.0,
      },
    });
  } catch (e) {
    console.log('[grafanaFaroSetup.ts] 그라파나 모니터링 초기화 실패: ', e);
  }
}
